import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, LogOut, Plus, Trash2, Pencil, Upload, X, Image as ImageIcon, Film,
  Check, Clock, Mic, Star, Video,
} from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Monogram } from "@/components/Monogram";

const TABS = ["Journal", "Events", "Gallery", "Books", "Giving", "Testimonies", "Prayers", "Bookings", "Site"];

function MediaUploader({ media, setMedia }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMedia([...(media || []), { url: data.url, type: data.type }]);
      toast.success("Uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-3">
        {(media || []).map((m, i) => (
          <div key={i} className="relative h-24 w-24 rounded-lg overflow-hidden border border-border">
            {m.type === "video" ? (
              <video src={mediaUrl(m.url)} className="h-full w-full object-cover" />
            ) : (
              <img src={mediaUrl(m.url)} alt="" className="h-full w-full object-cover" />
            )}
            <button
              type="button"
              onClick={() => setMedia(media.filter((_, idx) => idx !== i))}
              className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-0.5"
            >
              <X size={12} />
            </button>
          </div>
        ))}
      </div>
      <input ref={inputRef} type="file" accept="image/*,video/*" onChange={onFile} className="hidden" data-testid="media-file-input" />
      <button
        type="button"
        data-testid="media-upload-button"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border text-sm font-medium hover:border-gold disabled:opacity-60"
      >
        {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
        Upload photo / video
      </button>
    </div>
  );
}

const inputCls = "w-full px-3 py-2 rounded-lg bg-background border border-border focus:outline-none focus:ring-2 focus:ring-gold";
const label = "block text-sm font-medium mb-1.5";

function JournalManager() {
  const [posts, setPosts] = useState([]);
  const blank = { title: "", category: "Reflections", date: new Date().toISOString().slice(0, 10), read_time: "4 min read", excerpt: "", bodyText: "" };
  const [form, setForm] = useState(blank);
  const [editSlug, setEditSlug] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/blog").then((r) => setPosts(r.data));
  useEffect(() => { load(); }, []);

  const edit = async (slug) => {
    const { data } = await api.get(`/blog/${slug}`);
    setForm({ ...data, bodyText: (data.body || []).join("\n\n") });
    setEditSlug(slug);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      title: form.title, category: form.category, date: form.date,
      read_time: form.read_time, excerpt: form.excerpt,
      body: form.bodyText.split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editSlug) await api.put(`/admin/blog/${editSlug}`, payload);
      else await api.post("/admin/blog", payload);
      toast.success("Saved");
      setForm(blank); setEditSlug(null); load();
    } catch { toast.error("Save failed"); } finally { setBusy(false); }
  };

  const del = async (slug) => {
    if (!window.confirm("Delete this post?")) return;
    await api.delete(`/admin/blog/${slug}`); toast.success("Deleted"); load();
  };

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <form onSubmit={save} data-testid="journal-form" className="p-6 rounded-2xl border border-border bg-card space-y-4">
        <h3 className="font-serif text-xl font-semibold">{editSlug ? "Edit Post" : "New Post"}</h3>
        <div><label className={label}>Title</label><input className={inputCls} data-testid="journal-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className={label}>Category</label><input className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
          <div><label className={label}>Read time</label><input className={inputCls} value={form.read_time} onChange={(e) => setForm({ ...form, read_time: e.target.value })} /></div>
        </div>
        <div><label className={label}>Date</label><input type="date" className={inputCls} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
        <div><label className={label}>Excerpt</label><textarea className={inputCls} rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} /></div>
        <div><label className={label}>Body (separate paragraphs with a blank line)</label><textarea className={inputCls} rows={8} data-testid="journal-body" value={form.bodyText} onChange={(e) => setForm({ ...form, bodyText: e.target.value })} /></div>
        <div className="flex gap-3">
          <button type="submit" disabled={busy} data-testid="journal-save" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-ink text-parchment dark:bg-gold dark:text-ink font-semibold disabled:opacity-60">{busy ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}{editSlug ? "Update" : "Publish"}</button>
          {editSlug && <button type="button" onClick={() => { setForm(blank); setEditSlug(null); }} className="px-5 py-2.5 rounded-full border border-border font-medium">Cancel</button>}
        </div>
      </form>
      <div className="space-y-3">
        <h3 className="font-serif text-xl font-semibold">Published ({posts.length})</h3>
        {posts.map((p) => (
          <div key={p.slug} data-testid={`journal-item-${p.slug}`} className="flex items-center justify-between p-4 rounded-xl border border-border bg-card">
            <div><div className="font-medium">{p.title}</div><div className="text-xs text-muted-foreground">{p.category} · {p.date}</div></div>
            <div className="flex gap-2">
              <button onClick={() => edit(p.slug)} className="p-2 rounded-lg hover:bg-secondary" title="Edit"><Pencil size={15} /></button>
              <button onClick={() => del(p.slug)} data-testid={`journal-delete-${p.slug}`} className="p-2 rounded-lg hover:bg-secondary text-red-500" title="Delete"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function EventsManager() {
  const [events, setEvents] = useState([]);
  const blank = { type: "Revival", title: "", venue: "", location: "", date: new Date().toISOString().slice(0, 10), time: "", summary: "", status: "Upcoming", media: [] };
  const [form, setForm] = useState(blank);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/events").then((r) => setEvents(r.data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      if (editId) await api.put(`/admin/events/${editId}`, form);
      else await api.post("/admin/events", form);
      toast.success("Saved"); setForm(blank); setEditId(null); load();
    } catch { toast.error("Save failed"); } finally { setBusy(false); }
  };
  const edit = (ev) => { setForm({ ...blank, ...ev }); setEditId(ev.id); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const del = async (id) => { if (!window.confirm("Delete this event?")) return; await api.delete(`/admin/events/${id}`); toast.success("Deleted"); load(); };

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <form onSubmit={save} data-testid="event-form" className="p-6 rounded-2xl border border-border bg-card space-y-4">
        <h3 className="font-serif text-xl font-semibold">{editId ? "Edit Event" : "New Event"}</h3>
        <div className="grid grid-cols-2 gap-3">
          <div><label className={label}>Type</label><select className={inputCls} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>{["Revival", "Book Signing", "Keynote", "Other"].map((t) => <option key={t}>{t}</option>)}</select></div>
          <div><label className={label}>Status</label><select className={inputCls} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>{["Upcoming", "Past"].map((t) => <option key={t}>{t}</option>)}</select></div>
        </div>
        <div><label className={label}>Title</label><input className={inputCls} data-testid="event-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className={label}>Venue</label><input className={inputCls} value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} /></div>
          <div><label className={label}>Location</label><input className={inputCls} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className={label}>Date</label><input type="date" className={inputCls} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
          <div><label className={label}>Time</label><input className={inputCls} placeholder="6:30 PM" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} /></div>
        </div>
        <div><label className={label}>Summary</label><textarea className={inputCls} rows={3} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} /></div>
        <div><label className={label}>Event media (photos / videos)</label><MediaUploader media={form.media} setMedia={(m) => setForm({ ...form, media: m })} /></div>
        <div className="flex gap-3">
          <button type="submit" disabled={busy} data-testid="event-save" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-ink text-parchment dark:bg-gold dark:text-ink font-semibold disabled:opacity-60">{busy ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}{editId ? "Update" : "Add Event"}</button>
          {editId && <button type="button" onClick={() => { setForm(blank); setEditId(null); }} className="px-5 py-2.5 rounded-full border border-border font-medium">Cancel</button>}
        </div>
      </form>
      <div className="space-y-3">
        <h3 className="font-serif text-xl font-semibold">Events ({events.length})</h3>
        {events.map((ev) => (
          <div key={ev.id} data-testid={`event-item-${ev.id}`} className="flex items-center justify-between p-4 rounded-xl border border-border bg-card">
            <div><div className="font-medium">{ev.title}</div><div className="text-xs text-muted-foreground">{ev.type} · {ev.date} · {ev.location}{ev.media?.length ? ` · ${ev.media.length} media` : ""}</div></div>
            <div className="flex gap-2">
              <button onClick={() => edit(ev)} className="p-2 rounded-lg hover:bg-secondary"><Pencil size={15} /></button>
              <button onClick={() => del(ev.id)} data-testid={`event-delete-${ev.id}`} className="p-2 rounded-lg hover:bg-secondary text-red-500"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function GalleryManager() {
  const [items, setItems] = useState([]);
  const blank = { title: "", caption: "", media: [] };
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/gallery").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!form.media.length) { toast.error("Add at least one photo or video"); return; }
    setBusy(true);
    try { await api.post("/admin/gallery", form); toast.success("Added to gallery"); setForm(blank); load(); }
    catch { toast.error("Save failed"); } finally { setBusy(false); }
  };
  const del = async (id) => { if (!window.confirm("Remove from gallery?")) return; await api.delete(`/admin/gallery/${id}`); toast.success("Removed"); load(); };

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <form onSubmit={save} data-testid="gallery-form" className="p-6 rounded-2xl border border-border bg-card space-y-4">
        <h3 className="font-serif text-xl font-semibold">Add to Media & Gallery</h3>
        <div><label className={label}>Title (optional)</label><input className={inputCls} data-testid="gallery-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
        <div><label className={label}>Caption (optional)</label><input className={inputCls} value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} /></div>
        <div><label className={label}>Photos / videos</label><MediaUploader media={form.media} setMedia={(m) => setForm({ ...form, media: m })} /></div>
        <button type="submit" disabled={busy} data-testid="gallery-save" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-ink text-parchment dark:bg-gold dark:text-ink font-semibold disabled:opacity-60">{busy ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}Add</button>
      </form>
      <div className="space-y-3">
        <h3 className="font-serif text-xl font-semibold">Gallery ({items.length})</h3>
        <div className="grid grid-cols-2 gap-3">
          {items.map((it) => (
            <div key={it.id} data-testid={`gallery-item-${it.id}`} className="relative rounded-xl overflow-hidden border border-border">
              {it.media?.[0]?.type === "video" ? (
                <video src={mediaUrl(it.media[0].url)} className="h-32 w-full object-cover" />
              ) : (
                <img src={mediaUrl(it.media?.[0]?.url)} alt={it.title} className="h-32 w-full object-cover" />
              )}
              <div className="p-2 text-xs">{it.title || "Untitled"} {it.media?.length > 1 ? `(+${it.media.length - 1})` : ""}</div>
              <button onClick={() => del(it.id)} data-testid={`gallery-delete-${it.id}`} className="absolute top-2 right-2 bg-black/70 text-white rounded-full p-1"><Trash2 size={13} /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function BooksManager() {
  const [books, setBooks] = useState([]);
  const blank = { title: "", subtitle: "", year: "", category: "Faith & Revival", description: "", excerpt: "", accent: "#C5A059" };
  const [form, setForm] = useState(blank);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/books").then((r) => setBooks(r.data));
  useEffect(() => { load(); }, []);
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      if (editId) await api.put(`/admin/books/${editId}`, form);
      else await api.post("/admin/books", form);
      toast.success("Saved"); setForm(blank); setEditId(null); load();
    } catch { toast.error("Save failed"); } finally { setBusy(false); }
  };
  const edit = (b) => { setForm({ ...blank, ...b }); setEditId(b.id); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const del = async (id) => { if (!window.confirm("Delete this book?")) return; await api.delete(`/admin/books/${id}`); toast.success("Deleted"); load(); };
  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <form onSubmit={save} data-testid="book-form" className="p-6 rounded-2xl border border-border bg-card space-y-4">
        <h3 className="font-serif text-xl font-semibold">{editId ? "Edit Book" : "New Book"}</h3>
        <div><label className={label}>Title</label><input className={inputCls} data-testid="book-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className={label}>Subtitle</label><input className={inputCls} value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} /></div>
          <div><label className={label}>Year</label><input className={inputCls} value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} /></div>
        </div>
        <div className="grid grid-cols-2 gap-3 items-end">
          <div><label className={label}>Category</label><input className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
          <div><label className={label}>Cover accent</label><input type="color" className="h-10 w-full rounded-lg border border-border bg-background" value={form.accent} onChange={(e) => setForm({ ...form, accent: e.target.value })} /></div>
        </div>
        <div><label className={label}>Description</label><textarea className={inputCls} rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
        <div><label className={label}>Excerpt / quote</label><textarea className={inputCls} rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} /></div>
        <div className="flex gap-3">
          <button type="submit" disabled={busy} data-testid="book-save" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-ink text-parchment dark:bg-gold dark:text-ink font-semibold disabled:opacity-60">{busy ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}{editId ? "Update" : "Add Book"}</button>
          {editId && <button type="button" onClick={() => { setForm(blank); setEditId(null); }} className="px-5 py-2.5 rounded-full border border-border font-medium">Cancel</button>}
        </div>
      </form>
      <div className="space-y-3">
        <h3 className="font-serif text-xl font-semibold">Books ({books.length})</h3>
        {books.map((b) => (
          <div key={b.id} data-testid={`book-item-${b.id}`} className="flex items-center justify-between p-4 rounded-xl border border-border bg-card">
            <div className="flex items-center gap-3">
              <span className="h-8 w-8 rounded" style={{ background: b.accent }} />
              <div><div className="font-medium">{b.title}</div><div className="text-xs text-muted-foreground">{b.category} · {b.year}</div></div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => edit(b)} className="p-2 rounded-lg hover:bg-secondary"><Pencil size={15} /></button>
              <button onClick={() => del(b.id)} data-testid={`book-delete-${b.id}`} className="p-2 rounded-lg hover:bg-secondary text-red-500"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const CONTENT_FIELDS = [
  ["hero_tagline", "Hero tagline", true],
  ["about_heading", "About — heading", false],
  ["about_p1", "About — paragraph 1", true],
  ["about_p2", "About — paragraph 2", true],
  ["beliefs_intro", "Beliefs — intro", true],
];
const SOCIAL_FIELDS = [
  ["facebook", "Facebook URL"], ["instagram", "Instagram URL"], ["youtube", "YouTube URL"],
  ["tiktok", "TikTok URL"], ["x", "X (Twitter) URL"], ["linkedin", "LinkedIn URL"], ["whatsapp", "WhatsApp link"],
];

function SiteManager() {
  const [content, setContent] = useState(null);
  const [social, setSocial] = useState(null);
  const [stats, setStats] = useState({ views: 0, follows: 0 });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/settings").then((r) => { setContent(r.data.content); setSocial(r.data.social); setStats(r.data.stats); });
  }, []);
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.put("/admin/settings", { content, social }); toast.success("Site settings saved"); }
    catch { toast.error("Save failed"); } finally { setBusy(false); }
  };
  if (!content || !social) return <div className="text-muted-foreground">Loading…</div>;
  return (
    <form onSubmit={save} data-testid="site-form" className="max-w-3xl space-y-8">
      <div className="grid grid-cols-2 gap-4">
        <div className="p-5 rounded-2xl border border-border bg-card"><div className="text-xs text-muted-foreground">Page views</div><div className="font-serif text-3xl font-semibold" data-testid="stat-views">{stats.views}</div></div>
        <div className="p-5 rounded-2xl border border-border bg-card"><div className="text-xs text-muted-foreground">Followers</div><div className="font-serif text-3xl font-semibold" data-testid="stat-follows">{stats.follows}</div></div>
      </div>

      <div className="p-6 rounded-2xl border border-border bg-card space-y-4">
        <h3 className="font-serif text-xl font-semibold">Page Content</h3>
        {CONTENT_FIELDS.map(([k, lbl, multi]) => (
          <div key={k}>
            <label className={label}>{lbl}</label>
            {multi
              ? <textarea className={inputCls} rows={3} data-testid={`content-${k}`} value={content[k] || ""} onChange={(e) => setContent({ ...content, [k]: e.target.value })} />
              : <input className={inputCls} data-testid={`content-${k}`} value={content[k] || ""} onChange={(e) => setContent({ ...content, [k]: e.target.value })} />}
          </div>
        ))}
      </div>

      <div className="p-6 rounded-2xl border border-border bg-card space-y-4">
        <h3 className="font-serif text-xl font-semibold">Social Media Links</h3>
        <p className="text-sm text-muted-foreground">Paste your full profile URLs. Only the ones you fill in will show on the site.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          {SOCIAL_FIELDS.map(([k, lbl]) => (
            <div key={k}>
              <label className={label}>{lbl}</label>
              <input className={inputCls} data-testid={`social-${k}`} placeholder="https://…" value={social[k] || ""} onChange={(e) => setSocial({ ...social, [k]: e.target.value })} />
            </div>
          ))}
        </div>
      </div>

      <button type="submit" disabled={busy} data-testid="site-save" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-ink text-parchment dark:bg-gold dark:text-ink font-semibold disabled:opacity-60">{busy ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}Save Site Settings</button>
    </form>
  );
}

function GivingManager() {
  const [tiers, setTiers] = useState([]);
  const blank = { name: "", description: "", accent: "#C5A059", order: 0 };
  const [form, setForm] = useState(blank);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/giving-tiers").then((r) => setTiers(r.data));
  useEffect(() => { load(); }, []);
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      if (editId) await api.put(`/admin/giving-tiers/${editId}`, form);
      else await api.post("/admin/giving-tiers", form);
      toast.success("Saved"); setForm(blank); setEditId(null); load();
    } catch { toast.error("Save failed"); } finally { setBusy(false); }
  };
  const edit = (t) => { setForm({ ...blank, ...t }); setEditId(t.id); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const del = async (id) => { if (!window.confirm("Delete this giving level?")) return; await api.delete(`/admin/giving-tiers/${id}`); toast.success("Deleted"); load(); };
  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <form onSubmit={save} data-testid="giving-form" className="p-6 rounded-2xl border border-border bg-card space-y-4">
        <h3 className="font-serif text-xl font-semibold">{editId ? "Edit Giving Level" : "New Giving Level"}</h3>
        <div className="grid grid-cols-2 gap-3 items-end">
          <div><label className={label}>Name</label><input className={inputCls} data-testid="giving-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></div>
          <div><label className={label}>Accent color</label><input type="color" className="h-10 w-full rounded-lg border border-border bg-background" value={form.accent} onChange={(e) => setForm({ ...form, accent: e.target.value })} /></div>
        </div>
        <div><label className={label}>Description</label><textarea className={inputCls} rows={3} data-testid="giving-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
        <div><label className={label}>Order (lower shows first)</label><input type="number" className={inputCls} value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} /></div>
        <div className="flex gap-3">
          <button type="submit" disabled={busy} data-testid="giving-save" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-ink text-parchment dark:bg-gold dark:text-ink font-semibold disabled:opacity-60">{busy ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}{editId ? "Update" : "Add Level"}</button>
          {editId && <button type="button" onClick={() => { setForm(blank); setEditId(null); }} className="px-5 py-2.5 rounded-full border border-border font-medium">Cancel</button>}
        </div>
      </form>
      <div className="space-y-3">
        <h3 className="font-serif text-xl font-semibold">Giving Levels ({tiers.length})</h3>
        {tiers.map((t) => (
          <div key={t.id} data-testid={`giving-item-${t.id}`} className="flex items-center justify-between p-4 rounded-xl border border-border bg-card">
            <div className="flex items-center gap-3">
              <span className="h-8 w-8 rounded" style={{ background: t.accent }} />
              <div><div className="font-medium">{t.name}</div><div className="text-xs text-muted-foreground line-clamp-1 max-w-xs">{t.description}</div></div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => edit(t)} className="p-2 rounded-lg hover:bg-secondary"><Pencil size={15} /></button>
              <button onClick={() => del(t.id)} data-testid={`giving-delete-${t.id}`} className="p-2 rounded-lg hover:bg-secondary text-red-500"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TestimoniesManager() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/testimonies").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const approve = async (id) => { await api.put(`/admin/testimonies/${id}/approve`); toast.success("Approved & published"); load(); };
  const del = async (id) => { if (!window.confirm("Delete this testimony?")) return; await api.delete(`/admin/testimonies/${id}`); toast.success("Deleted"); load(); };
  const pending = items.filter((t) => t.status !== "approved");
  const approved = items.filter((t) => t.status === "approved");
  const Card = ({ t }) => (
    <div key={t.id} data-testid={`testimony-item-${t.id}`} className="p-5 rounded-2xl border border-border bg-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="font-semibold">{t.name}{t.location ? <span className="text-xs text-muted-foreground font-normal"> · {t.location}</span> : null}</div>
          <p className="mt-2 text-sm text-foreground/90 whitespace-pre-wrap">{t.body}</p>
          <div className="mt-2 text-xs text-muted-foreground">Received {t.created_at ? t.created_at.slice(0, 10) : ""}</div>
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          {t.status !== "approved" && (
            <button onClick={() => approve(t.id)} data-testid={`testimony-approve-${t.id}`} className="p-2 rounded-lg hover:bg-secondary text-green-600" title="Approve"><Check size={16} /></button>
          )}
          <button onClick={() => del(t.id)} data-testid={`testimony-delete-${t.id}`} className="p-2 rounded-lg hover:bg-secondary text-red-500" title="Delete"><Trash2 size={15} /></button>
        </div>
      </div>
    </div>
  );
  return (
    <div className="max-w-4xl space-y-8">
      <div className="space-y-4">
        <h3 className="font-serif text-xl font-semibold flex items-center gap-2"><Clock size={18} /> Pending Review ({pending.length})</h3>
        {pending.length === 0 && <p className="text-muted-foreground text-sm">No testimonies awaiting review.</p>}
        {pending.map((t) => <Card key={t.id} t={t} />)}
      </div>
      <div className="space-y-4">
        <h3 className="font-serif text-xl font-semibold flex items-center gap-2"><Check size={18} /> Published ({approved.length})</h3>
        {approved.length === 0 && <p className="text-muted-foreground text-sm">No published testimonies yet.</p>}
        {approved.map((t) => <Card key={t.id} t={t} />)}
      </div>
    </div>
  );
}

function BookingsManager() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/appointments").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const del = async (id) => { if (!window.confirm("Delete this request?")) return; await api.delete(`/admin/appointments/${id}`); toast.success("Deleted"); load(); };
  return (
    <div className="space-y-4 max-w-4xl">
      <h3 className="font-serif text-xl font-semibold">Appointment Requests ({items.length})</h3>
      {items.length === 0 && <p className="text-muted-foreground text-sm">No requests yet.</p>}
      {items.map((a) => (
        <div key={a.id} data-testid={`appt-item-${a.id}`} className="p-5 rounded-2xl border border-border bg-card">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="font-semibold">{a.name} <span className="text-xs text-muted-foreground font-normal">· {a.email}{a.phone ? ` · ${a.phone}` : ""}</span></div>
              <div className="mt-1 text-sm"><span className="px-2 py-0.5 rounded-full bg-gold/15 text-gold-deep dark:text-gold-light text-xs font-medium">{a.concern}</span></div>
              <div className="mt-1 text-xs text-muted-foreground">Preferred: {a.preferred_date || "—"} {a.preferred_time || ""} · Received {a.created_at ? a.created_at.slice(0, 10) : ""}</div>
              {a.details && <p className="mt-3 text-sm text-foreground/90 whitespace-pre-wrap bg-secondary rounded-lg p-3">{a.details}</p>}
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              <a href={`mailto:${a.email}`} className="p-2 rounded-lg hover:bg-secondary" title="Reply"><Pencil size={15} /></a>
              <button onClick={() => del(a.id)} data-testid={`appt-delete-${a.id}`} className="p-2 rounded-lg hover:bg-secondary text-red-500"><Trash2 size={15} /></button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function SpokenPrayersManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", text: "", lipsync: false });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/admin/spoken-prayers").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!items.some((i) => i.status === "video_pending")) return;
    const t = setInterval(() => {
      api.get("/admin/spoken-prayers").then((r) => setItems(r.data)).catch(() => {});
    }, 10000);
    return () => clearInterval(t);
  }, [items]);

  const save = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Upload a recording (audio or video)"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", form.title);
      fd.append("text", form.text);
      fd.append("lipsync", form.lipsync ? "true" : "false");
      await api.post("/admin/spoken-prayers/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(form.lipsync
        ? "Uploaded — audio is live, the talking video is rendering…"
        : "Spoken prayer published");
      setForm({ title: "", text: "", lipsync: false });
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const setCover = async (id) => {
    await api.put(`/admin/spoken-prayers/${id}/cover`);
    toast.success("Set as hero cover"); load();
  };
  const del = async (id) => {
    if (!window.confirm("Delete this spoken prayer?")) return;
    await api.delete(`/admin/spoken-prayers/${id}`); toast.success("Deleted"); load();
  };

  const statusBadge = (p) => {
    if (p.status === "video_pending")
      return <span className="inline-flex items-center gap-1 text-xs text-amber-600"><Loader2 size={12} className="animate-spin" /> Video rendering…</span>;
    if (p.video_url)
      return <span className="inline-flex items-center gap-1 text-xs text-green-600"><Video size={12} /> Audio + video</span>;
    if (p.video_failed)
      return <span className="text-xs text-muted-foreground">Audio ready · video failed</span>;
    return <span className="inline-flex items-center gap-1 text-xs text-green-600"><Check size={12} /> Audio ready</span>;
  };

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <form onSubmit={save} data-testid="spoken-prayer-form" className="p-6 rounded-2xl border border-border bg-card space-y-4">
        <h3 className="font-serif text-xl font-semibold flex items-center gap-2"><Mic size={18} /> New Spoken Prayer</h3>

        <div className="rounded-xl border border-gold/30 bg-gold/5 p-4 text-sm text-foreground/80">
          Upload a recording of Charles praying — <strong>audio or video, his real voice</strong>. For an audio
          recording you can optionally turn it into a talking video of Charles reciting it.
        </div>

        <div><label className={label}>Title (optional)</label><input className={inputCls} data-testid="spoken-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. A Prayer for Boldness" /></div>
        <div><label className={label}>Description shown under it (optional)</label><textarea className={inputCls} rows={4} data-testid="spoken-text" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} placeholder="A short line about this prayer…" /></div>
        <div>
          <label className={label}>Recording (audio or video)</label>
          <input ref={fileRef} type="file" accept="audio/*,video/*" data-testid="spoken-file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="block w-full text-sm file:mr-3 file:rounded-full file:border-0 file:bg-secondary file:px-4 file:py-2 file:text-sm file:font-medium" />
          {file && <p className="text-xs text-muted-foreground mt-1">{file.name}</p>}
        </div>
        {file && !(file.type.startsWith("video") || /\.(mp4|mov|webm|m4v)$/i.test(file.name)) && (
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" data-testid="spoken-lipsync" checked={form.lipsync} onChange={(e) => setForm({ ...form, lipsync: e.target.checked })} className="h-4 w-4 accent-[#C5A059]" />
            Also create a talking video of Charles reciting this audio <span className="text-muted-foreground">(a few minutes)</span>
          </label>
        )}
        <button type="submit" disabled={busy} data-testid="spoken-save" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-ink text-parchment dark:bg-gold dark:text-ink font-semibold disabled:opacity-60">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
          {busy ? "Uploading…" : "Publish Spoken Prayer"}
        </button>
      </form>

      <div className="space-y-3">
        <h3 className="font-serif text-xl font-semibold">Spoken Prayers ({items.length})</h3>
        {items.length === 0 && <p className="text-muted-foreground text-sm">No spoken prayers yet.</p>}
        {items.map((p) => (
          <div key={p.id} data-testid={`spoken-item-${p.id}`} className="p-4 rounded-xl border border-border bg-card space-y-2">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-medium flex items-center gap-2">{p.title}{p.is_cover && <span className="inline-flex items-center gap-1 text-xs text-gold-deep dark:text-gold-light"><Star size={12} className="fill-current" /> Cover</span>}</div>
                <div className="mt-0.5">{statusBadge(p)}</div>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => setCover(p.id)} data-testid={`spoken-cover-${p.id}`} className="p-2 rounded-lg hover:bg-secondary text-gold-deep dark:text-gold-light" title="Use as hero cover"><Star size={15} /></button>
                <button onClick={() => del(p.id)} data-testid={`spoken-delete-${p.id}`} className="p-2 rounded-lg hover:bg-secondary text-red-500" title="Delete"><Trash2 size={15} /></button>
              </div>
            </div>
            {p.video_url ? (
              <video src={p.video_url} controls className="w-full rounded-lg bg-ink" />
            ) : p.audio_url ? (
              <audio src={p.audio_url} controls className="w-full" />
            ) : null}
            <p className="text-xs text-muted-foreground line-clamp-2">{p.text}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("Journal");

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-20 border-b border-border bg-background/90 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Monogram size={34} />
            <div>
              <div className="font-serif font-semibold leading-tight">Dashboard</div>
              <div className="text-xs text-muted-foreground">{user?.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate("/")} className="text-sm px-4 py-2 rounded-full border border-border hover:border-gold">View site</button>
            <button onClick={() => { logout(); navigate("/login"); }} data-testid="logout-button" className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full bg-ink text-parchment dark:bg-gold dark:text-ink font-semibold"><LogOut size={15} />Logout</button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex flex-wrap gap-2 mb-8">
          {TABS.map((t) => (
            <button key={t} data-testid={`tab-${t.toLowerCase()}`} onClick={() => setTab(t)} className={`px-5 py-2 rounded-full text-sm font-medium border transition-colors ${tab === t ? "bg-ink text-parchment border-ink dark:bg-gold dark:text-ink dark:border-gold" : "border-border hover:border-gold"}`}>
              {t}
            </button>
          ))}
        </div>
        {tab === "Journal" && <JournalManager />}
        {tab === "Events" && <EventsManager />}
        {tab === "Gallery" && <GalleryManager />}
        {tab === "Books" && <BooksManager />}
        {tab === "Giving" && <GivingManager />}
        {tab === "Testimonies" && <TestimoniesManager />}
        {tab === "Prayers" && <SpokenPrayersManager />}
        {tab === "Bookings" && <BookingsManager />}
        {tab === "Site" && <SiteManager />}
      </div>
    </div>
  );
}
